import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useSessionTimeout from '../hooks/useSessionTimeout';

function Navbar() {
  useSessionTimeout(); // timeout de 20 min por inactividad + cierre de sesión al cerrar ventana

  const navigate = useNavigate();
  const location = useLocation();
  const usuario = JSON.parse(localStorage.getItem('usuario') || 'null');

  const [abierto, setAbierto] = useState(false);
  const menuRef = useRef(null);

  // Cerrar al clickear afuera o con Escape
  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setAbierto(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    navigate('/login');
  };

  const esActivo = (path) => location.pathname === path;

  const ir = (path) => {
    setAbierto(false);
    navigate(path);
  };

  // Opciones según rol
  const opciones = [
    { label: 'Dashboard', path: '/dashboard', visible: true },
    { label: 'Reportes', path: '/reportes', visible: true },
    { label: 'Administración', path: '/admin', visible: usuario?.rol === 'superadmin' },
    { label: 'Mi empresa', path: '/mi-empresa', visible: usuario?.rol === 'admin_empresa' },
    { label: 'Perfil', path: '/perfil', visible: true },
  ].filter(o => o.visible);

  return (
    <nav className="bacmt-nav">
      {/* Estilos responsive (las media queries no corren con estilos inline) */}
      <style>{CSS}</style>

      <div className="bacmt-izquierda">
        <img alt="BAC MT" src="/img/BAC-MT.png" className="bacmt-logo-img" />
        <div>
          <div className="bacmt-logo-titulo">BAC MT</div>
          <div className="bacmt-logo-sub">Sistema de Monitoreo</div>
        </div>
      </div>

      <div className="bacmt-derecha" ref={menuRef}>
        <button
          className="bacmt-trigger"
          onClick={() => setAbierto(v => !v)}
          aria-haspopup="true"
          aria-expanded={abierto}
        >
          <div className="bacmt-usuario-info">
            <div className="bacmt-usuario-nombre">{usuario?.nombre}</div>
            <div className="bacmt-usuario-rol">{usuario?.rol?.toUpperCase()}</div>
          </div>
          <div className="bacmt-avatar">{usuario?.nombre?.[0]?.toUpperCase()}</div>
          <span className={`bacmt-chevron ${abierto ? 'abierto' : ''}`}>▾</span>
        </button>

        {abierto && (
          <div className="bacmt-menu" role="menu">
            <div className="bacmt-menu-header">
              <div className="bacmt-menu-header-nombre">{usuario?.nombre}</div>
              <div className="bacmt-menu-header-rol">{usuario?.rol?.toUpperCase()}</div>
            </div>

            <div className="bacmt-menu-items">
              {opciones.map(o => (
                <button
                  key={o.path}
                  role="menuitem"
                  className={`bacmt-menu-item ${esActivo(o.path) ? 'activo' : ''}`}
                  onClick={() => ir(o.path)}
                >
                  {o.label}
                  {esActivo(o.path) && <span className="bacmt-dot-activo" />}
                </button>
              ))}
            </div>

            <div className="bacmt-menu-sep" />

            <button className="bacmt-menu-logout" onClick={handleLogout} role="menuitem">
              Salir
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}

const CSS = `
.bacmt-nav {
  display: flex; align-items: center; justify-content: space-between;
  background-color: chocolate;
 border-bottom: 1px solid var(--border);
  padding: 0 28px; height: 64px; position: sticky; top: 0; z-index: 100;
}
.bacmt-izquierda { display: flex; align-items: center; gap: 12px; min-width: 0; }
.bacmt-logo-img { width: 70px; flex-shrink: 0; }
.bacmt-logo-titulo { font-size: 0.95rem; font-weight: 600; color: var(--text-primary); letter-spacing: 0.04em; }
.bacmt-logo-sub { font-size: 0.7rem;white-space: nowrap; }

.bacmt-derecha { position: relative; flex-shrink: 0; }
.bacmt-trigger {
  display: flex; align-items: center; gap: 12px;
  background: #162340; border: 1px solid transparent; border-radius: 10px;
  padding: 6px 10px; cursor: pointer; transition: all 0.15s;
}
.bacmt-trigger:hover { border-color: var(--border); }
.bacmt-usuario-info { text-align: right; }
.bacmt-usuario-nombre { font-size: 0.875rem; color: var(--text-primary); font-weight: 500; white-space: nowrap; }
.bacmt-usuario-rol { font-size: 0.65rem; color: var(--accent-cyan); letter-spacing: 0.08em; }
.bacmt-avatar {
  width: 34px; height: 34px; background-color: var(--accent-glow);
  border: 1px solid var(--accent); border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.9rem; font-weight: 600; color: var(--accent); flex-shrink: 0;
}
.bacmt-chevron { color: var(--text-muted); font-size: 0.7rem; transition: transform 0.18s; display: inline-block; }
.bacmt-chevron.abierto { transform: rotate(180deg); }

.bacmt-menu {
  position: absolute; top: calc(100% + 8px); right: 0;
  min-width: 220px; background-color: var(--bg-card);
  border: 1px solid var(--border); border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.35); padding: 6px; z-index: 200;
  transform-origin: top right; animation: bacmtPop 0.14s ease-out;
}
@keyframes bacmtPop {
  from { opacity: 0; transform: scale(0.96) translateY(-4px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}
.bacmt-menu-header { padding: 10px 12px 8px; }
.bacmt-menu-header-nombre { font-size: 0.875rem; font-weight: 600; color: var(--text-primary); }
.bacmt-menu-header-rol { font-size: 0.65rem; color: var(--accent-cyan); letter-spacing: 0.08em; margin-top: 2px; }

.bacmt-menu-items { display: flex; flex-direction: column; gap: 2px; padding-top: 4px; }
.bacmt-menu-item {
  display: flex; align-items: center; justify-content: space-between; width: 100%;
  background: none; border: none; cursor: pointer; color: var(--text-muted);
  padding: 9px 12px; border-radius: 8px; font-size: 0.875rem;
  font-family: 'IBM Plex Sans', sans-serif; text-align: left; transition: all 0.12s;
}
.bacmt-menu-item:hover {   background: linear-gradient(135deg, #ff9124 0%, #f15a24 100%);color: var(--text-primary); }
.bacmt-menu-item.activo { color: var(--accent); background-color: var(--accent-glow); }
.bacmt-dot-activo { width: 6px; height: 6px; border-radius: 50%; background-color: var(--accent); }

.bacmt-menu-sep { height: 1px; background-color: var(--border); margin: 6px 8px; }

.bacmt-menu-logout {
  width: 100%; background: none; border: none; cursor: pointer; color: var(--danger);
  padding: 9px 12px; border-radius: 8px; font-size: 0.875rem;
  font-family: 'IBM Plex Sans', sans-serif; text-align: left; font-weight: 500;
}
.bacmt-menu-logout:hover { background-color: rgba(240,77,94,0.08); }

/* ---- MOBILE ---- */
@media (max-width: 640px) {
  .bacmt-nav { padding: 0 14px; height: 58px; }
  .bacmt-logo-img { width: 48px; }
  .bacmt-logo-sub { display: none; }
  .bacmt-usuario-info { display: none; }      /* solo queda la bolita */
  .bacmt-chevron { display: none; }
  .bacmt-trigger { gap: 0; padding: 4px; }
  .bacmt-menu { min-width: 200px; }
}
`;

export default Navbar;